import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import * as d3 from "d3";
import "./chart.css";
import { MdLabel, MdLabelOff } from "react-icons/md";

const stackedBarCharts = forwardRef(({ xData, yData }, ref) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const [chartWidth, setChartWidth] = useState(window.innerWidth * 0.9);
  const [visibleFields, setVisibleFields] = useState(yData.map(d => d.field));
  const [showLegend, setShowLegend] = useState(false);
  const [legendSearch, setLegendSearch] = useState("");
  const [chartHeight, setChartHeight] = useState(window.innerHeight * 0.7);

  useImperativeHandle(ref, () => ({
    getUpdatedChartData: () => [...yData],
  }));

  useEffect(() => {
    const updateChartSize = () => {
      setChartWidth(window.innerWidth * 0.9);
      setChartHeight(window.innerHeight * 0.7);
    };
    updateChartSize();
    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, []);

  useEffect(() => {
    if (!xData || !yData || xData.length === 0 || yData.length === 0) return;
    const newFields = yData.map(d => d.field);
    // console.log('Setting visibleFields to:', newFields);
    setVisibleFields(newFields);
  }, [xData, yData]);

  useEffect(() => {
    if (!xData || !yData || xData.length === 0 || yData.length === 0) return;
    // console.log(xData);
    // console.log(yData);
    const margin = { top: 60, right: 20, bottom: 50, left: 50 };
    const height = chartHeight;
    const width = chartWidth;
    const tickInterval = Math.ceil(xData.length / 20);
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .style("background-color", "#F2F2F2");

    const filteredYData = yData.filter(d => visibleFields.includes(d.field));

    const xScale = d3.scaleBand()
      .domain(xData.map((_, i) => i))
      .range([margin.left, width - margin.right])
      .padding(xData.length > 50 ? 0.1 : 0.3);

    const maxY = d3.max(xData.map((_, i) => filteredYData.reduce((sum, d) => sum + (d.values[i] ?? 0), 0)));

    const yScale = d3.scaleLinear()
      .domain([0, maxY * 1.1])
      .range([height - margin.bottom, margin.top]);

    const xAxis = d3.axisBottom(xScale)
      .tickValues(xData.map((_, i) => i).filter(i => i % tickInterval === 0))
      .tickFormat(i => xData[i]);
    const yAxis = d3.axisLeft(yScale);

    svg.append("g")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(xAxis)
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    svg.append("g")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(yAxis)
      .selectAll("text")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    const tooltip = d3.select(tooltipRef.current)
      .style("opacity", 0)
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("background-color", "rgba(0,0,0,0.6)")
      .style("color", "white")
      .style("padding", "5px")
      .style("border-radius", "4px");

    const keys = filteredYData.map(d => d.field);
    const data = xData.map((_, i) => {
      const row = {};
      filteredYData.forEach(d => {
        row[d.field] = d.values[i] ?? 0;
      });
      return row;
    });

    const stack = d3.stack().keys(keys);
    const stackedData = stack(data);

    const chartGroup = svg.append("g").attr("class", "chart-content");

    chartGroup.selectAll("g.layer")
      .data(stackedData)
      .enter()
      .append("g")
      .attr("class", d => `layer layer-${d.key.replace(/\s+/g, '-')}`)
      .attr("fill", (d, i) => {
        const series = filteredYData.find(s => s.field === d.key);
        return series ? series.color : "gray";
      })
      .selectAll("rect")
      .data(d => d.map((dPoint, i) => ({ ...dPoint, index: i, key: d.key })))
      .enter()
      .append("rect")
      .attr("x", d => xScale(d.index))
      .attr("y", d => yScale(d[1]))
      .attr("height", d => yScale(d[0]) - yScale(d[1]))
      .attr("width", xScale.bandwidth())
       .on("mouseover", (event, d) => {
        const [x, y] = d3.pointer(event, svg.node());
        const series = filteredYData.find(s => s.field === d.key);
        tooltip.style("opacity", 1)
          .style("left", `${x + 10}px`)
          .style("top", `${y - 10}px`)
          .html(`
            <div style="color: white; font-size: 14px; font-weight: bold;">${xData[d.index]}</div>
            <div style="display: flex; align-items: center; margin-top: 2px;">
              <div style="width: 10px; height: 10px; background-color: ${series.color}; margin-right: 5px; border: 1px solid white;"></div>
              <span style="font-size: 13px; font-weight: bold;">${series.field}:</span>
              <span style="margin-left: 5px; font-size: 13px;">${(d[1] - d[0]).toLocaleString()}</span>
            </div>
          `);
      })
      .on("mouseout", () => tooltip.style("opacity", 0));
  }, [xData, yData, chartWidth, visibleFields]);

  return (
    <div className="chart-container" style={{ position: "relative", overflow: "visible" }}>
      {/* Button to toggle legend visibility */}
      <button
        onClick={() => setShowLegend(!showLegend)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          padding: "6px 12px",
          backgroundColor: "transparent",
          color: "black",
          borderRadius: "4px",
          cursor: "pointer"
        }}
      >
  {showLegend ? <MdLabelOff size={24} /> : <MdLabel size={24} />}
      </button>

      {/* Legend Sidebar */}
      {showLegend && (
        <div
          className="legend-sidebar"
          style={{
            position: "absolute",
            top: 50,
            right: 10,
            width: "250px",
            maxHeight: "400px",
            backgroundColor: "#fff",
            boxShadow: "0 0 10px rgba(0,0,0,0.2)",
            padding: "10px",
            overflowY: "auto",
            zIndex: 9,
          }}
        >
          <input
            type="text"
            value={legendSearch}
            onChange={(e) => setLegendSearch(e.target.value)}
            placeholder="Search..."
            style={{
              width: "100%",
              padding: "6px 8px",
              marginBottom: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />

          <div>
            {yData
              .filter(d => d.values.some(v => v !== 0))
              .filter(d =>
                d.field.toLowerCase().includes(legendSearch.toLowerCase())
              )
              .map(d => (
                <div
                  key={d.field}
                  onClick={() =>
                    setVisibleFields(prev =>
                      prev.includes(d.field)
                        ? prev.filter(f => f !== d.field)
                        : [...prev, d.field]
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: d.color,
                      marginRight: "10px",
                    }}
                  />
                  <span
                    style={{
                      textDecoration: visibleFields.includes(d.field)
                        ? "none"
                        : "line-through",
                    }}
                  >
                    {d.field}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}



      <svg ref={svgRef}></svg>
      <div ref={tooltipRef}></div>
    </div>
  );
});

export default stackedBarCharts;
